import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Tag,
  Space,
  Input,
  DatePicker,
  Select,
  Row,
  Col,
  Statistic,
  Button,
  Badge,
  Modal,
  Descriptions,
  Spin
} from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  SearchOutlined,
  EyeOutlined,
  FilterOutlined,
  ShoppingCartOutlined
} from "@ant-design/icons";
import { getUserTransactions } from "../../../services/dashboardclient/dashboardclient";
import { useTranslation } from "react-i18next";

// Styles CSS pour les cartes
const cardStyles = `
  .theme-card {
    background-color: var(--background-body);
    color: var(--primary-color);
    border: 1px solid var(--border-color);
  }
  
  .theme-card .ant-card-head {
    background-color: var(--background-body);
    color: var(--primary-color);
    border-bottom: 1px solid var(--border-color);
  }
  
  .theme-card .ant-card-head-title {
    color: var(--primary-color);
  }
  
  .theme-statistic .ant-statistic-title {
    color: var(--text-secondary);
  }
  
  .theme-statistic .ant-statistic-content {
    color: var(--primary-color);
  }
`;

const { RangePicker } = DatePicker;
const { Option } = Select;

const BalanceTransactions = () => {
  const { t } = useTranslation();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    transaction_type: 'all',
    status: 'all',
    startDate: null,
    endDate: null,
    search: '',
    page: 1,
    per_page: 15
  });
  const [summary, setSummary] = useState({});
  const [pagination, setPagination] = useState({});
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);

  useEffect(() => {
    // Ajouter les styles CSS
    const styleSheet = document.createElement("style");
    styleSheet.innerText = cardStyles;
    document.head.appendChild(styleSheet);

    fetchTransactions();

    return () => {
      // Nettoyer les styles lors du démontage du composant
      document.head.removeChild(styleSheet);
    };
  }, []);
  const statusTranslations = {
    'enattente': t('Pending'),
    'annule': t('Cancelled'),
    'paye': t('Paid'),
    'confirme': t('Paid')
  };
  const fetchTransactions = async () => {
    setLoading(true);
    try {
      let apiFilters = { ...filters };
      if (filters.status === 'complete') {
        apiFilters.status = ['paye', 'confirme'];
      } else if (filters.status !== 'all') {
        const statusMap = { 'enattente': 'en attente', 'annule': 'annulé' };
        apiFilters.status = statusMap[filters.status] || filters.status;
      }
      const response = await getUserTransactions(apiFilters);
      setTransactions(response.data);
      setSummary(response.summary);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTransactions(); }, [filters]);

  const handleFilterChange = (key, value) => setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  const handleDateChange = (dates) => setFilters(prev => ({ ...prev, startDate: dates ? dates[0] : null, endDate: dates ? dates[1] : null, page: 1 }));
  const handleSearchChange = (e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }));
  const handlePageChange = (page) => setFilters(prev => ({ ...prev, page }));

  const getStatusBadge = (status) => {
    const statusMap = {
      'confirme': 'success',
      'paye': 'success',
      'completed': 'success',
      'pending': 'processing',
      'en attente': 'processing',
      'cancelled': 'error',
      'annulé': 'error',
      'refunded': 'warning',
      'complété': 'success'
    };
    return statusMap[status] || 'default';
  };

  const columns = [
    {
      title: t('Date'),
      dataIndex: 'date',
      key: 'date',
      render: (date) => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.date) - new Date(b.date)
    },
    {
      title: t('Type'),
      dataIndex: 'type',
      key: 'type',
      render: (type) => <Tag color={type === 'achat' ? 'blue' : 'green'}>{type === 'achat' ? t('Purchase') : t('Sale')}</Tag>
    },
    { title: t('Description'), dataIndex: 'titre', key: 'titre' },
    {
      title: t('Amount'),
      dataIndex: 'montant',
      key: 'montant',
      render: (amount, record) => (
        <span style={{ color: record.type === 'vente' ? '#52c41a' : '', fontWeight: 'bold' }}>
          {record.type === 'vente' ? `+${amount} $` : (record.formatted_montant || `${amount} $`)}
        </span>
      )
    },
    {
      title: t('Status'),
      dataIndex: 'etat',
      key: 'etat',
      render: (status) => {
        const displayStatus = statusTranslations[status] || status;
        return (
          <Badge
            style={{ color: "var(--primary-color3)!important" }}
            status={getStatusBadge(status)}
            text={displayStatus}
          />
        );
      }
    },

    {
      title: t('Actions'),
      key: 'actions',
      render: (_, record) => (
        <Button icon={<EyeOutlined />} size="small" onClick={() => { setSelectedTransaction(record); setDetailVisible(true); }}>
          {t('Details')}
        </Button>
      )
    }
  ];

  if (loading && transactions.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (


    <div style={{ padding: '24px' }}>

      {/* <span  className="mb-3" style={{ color: "var(--primary-color)", fontSize: "20px",fontWeight: "bold" ,marginBottom: '24px !important'}}>
          {t("My Balance")}
        </span>  */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={8} style={{
          backgroundColor: "var(--background-body) !important",

        }}  >
          <Card style={{
            backgroundColor: "var(--primary-color7)",
            border: "7px solid var(--primary-color6)",
            //  border: "var(--primary-color6)"

          }} className="theme-card theme-statistic">
            <Statistic style={{

              color: "var(--primary-color) !important",
              border: "var(--primary-color6)"
            }} title={t('Total_Purchases')} value={summary.total_achats || 0} precision={2} prefix="$" />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card className="theme-card theme-statistic" style={{
            backgroundColor: "var(--primary-color7)",
            border: "7px solid var(--primary-color6)",

            // border: "10px solid var(--primary-color6)",
            //  border: "var(--primary-color6)"

          }}>
            <Statistic style={{
              backgroundColor: "var(--primary-color7)",
              color: "var(--primary-color) !important",
              // border: "var(--primary-color6)"
            }} title={t('Total_Sales')} value={summary.total_ventes || 0} precision={2} prefix="$" />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card className="theme-card theme-statistic" style={{
            backgroundColor: "var(--primary-color7)",
            border: "7px solid var(--primary-color6)",
            // border: "10px solid var(--primary-color6)",
            //  border: "var(--primary-color6)"

          }}>
            <Statistic style={{
              backgroundColor: "var(--primary-color7)",
              color: "var(--primary-color) !important",
              // border: "var(--primary-color6)"
            }}
              title={t('Balance')}
              value={summary.solde || 0}
              precision={2}
              prefix="$"
              valueStyle={{ color: (summary.solde || 0) >= 0 ? '#52c41a' : 'var(--primary-color7)' }}
            />
          </Card>
        </Col>
      </Row>

      <Card className="theme-card" style={{ marginBottom: '16px' }}>
        <Row gutter={16}>
          <Col xs={24} sm={6}>
            <Select
              placeholder={t('Transaction_Type')}
              style={{ width: '100%' }}
              value={filters.transaction_type}
              onChange={(v) => handleFilterChange('transaction_type', v)}
              className="theme-select"
              suffixIcon={<FilterOutlined style={{ color: "var(--primary-color) !important" }} />}
            >
              <Option value="all">{t('All')}</Option>
              <Option value="achats">{t('Purchase')}</Option>
              <Option value="ventes">{t('Sale')}</Option>
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <Select
              placeholder={t('Status')}
              style={{ width: '100%' }}
              value={filters.status}
              onChange={(v) => handleFilterChange('status', v)}
              className="theme-select"
              suffixIcon={<FilterOutlined style={{ color: "var(--primary-color) !important" }} />}
            >
              <Option value="all">{t("AllStatuses")}</Option>
              <Option value="complete">{t('Completed')}</Option>
              <Option value="enattente">{t('Pending')}</Option>
              <Option value="annule">{t('Cancelled')}</Option>
            </Select>
          </Col>
          <Col xs={24} sm={8}>
            <RangePicker style={{ width: '100%' }} onChange={handleDateChange} className="theme-date-picker" />
          </Col>
          <Col xs={24} sm={4}>
            <Input
              placeholder={t('Search')}
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={handleSearchChange}
              className="theme-input"
            />
          </Col>
        </Row>
      </Card>

      <Card className="theme-card" title={`${t('Transactions')} (${summary.nombre_total || 0})`}>
        <Table
          columns={columns}
          dataSource={transactions}
          loading={loading}
          pagination={{
            current: pagination.current_page,
            pageSize: pagination.per_page,
            total: pagination.total,
            onChange: handlePageChange
          }}
          scroll={{ x: 800 }}
          className="theme-table"
        />
      </Card>

      <Modal
        title={t('Transaction_Details')}
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={700}
        className="theme-modal"
      >
        {/* {selectedTransaction && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label={t('Type')}>
              <Tag color={selectedTransaction.type === 'achat' ? 'blue' : 'green'}>
                {selectedTransaction.type === 'achat' ? t('Purchase') : t('Sale')}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('Date')}>
              {new Date(selectedTransaction.date).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label={t('Description')}>
              {selectedTransaction.titre}
            </Descriptions.Item>
            <Descriptions.Item label={t('Amount')}>
              <span style={{ color: selectedTransaction.type === 'vente' ? '#52c41a' : '', fontWeight: 'bold' }}>
                {selectedTransaction.type === 'vente' ? '+' : '-'}${selectedTransaction.montant}
              </span>
            </Descriptions.Item>
           <Descriptions.Item label={t('Status')}>
  <Badge
    status={getStatusBadge(selectedTransaction.etat)}
    text={statusTranslations[selectedTransaction.etat] || selectedTransaction.etat}
  />
</Descriptions.Item>

            <Descriptions.Item label={t('Login')}>
              {selectedTransaction.login}
            </Descriptions.Item>
          </Descriptions>
        )} */}
        {selectedTransaction && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label={t('Type')} style={{ color: 'var(--primary-color3)' }}>
              <Tag color={selectedTransaction.type === 'achat' ? 'blue' : 'green'}>
                {selectedTransaction.type === 'achat' ? t('Purchase') : t('Sale')}
              </Tag>
            </Descriptions.Item>

            <Descriptions.Item label={t('Date')} style={{ color: 'var(--primary-color3)' }}>
              <span style={{ color: 'var(--primary-color3)' }}>
                {new Date(selectedTransaction.date).toLocaleString()}
              </span>
            </Descriptions.Item>

            <Descriptions.Item label={t('Description')} style={{ color: 'var(--primary-color3)' }}>
              <span style={{ color: 'var(--primary-color3)' }}>
                {selectedTransaction.titre}
              </span>
            </Descriptions.Item>

            <Descriptions.Item label={t('Amount')} style={{ color: 'var(--primary-color3)' }}>
              <span
                style={{
                  color: selectedTransaction.type === 'vente' ? '#52c41a' : 'var(--primary-color3)',
                  fontWeight: 'bold'
                }}
              >
                {selectedTransaction.type === 'vente' ? `+${selectedTransaction.montant} $` : (selectedTransaction.formatted_montant || `${selectedTransaction.montant} $`)}
              </span>
            </Descriptions.Item>

            <Descriptions.Item label={t('Status')} style={{ color: 'var(--primary-color3)' }}>
              <Badge style={{ color: 'var(--primary-color3)' }}
                status={getStatusBadge(selectedTransaction.etat)}
                text={statusTranslations[selectedTransaction.etat] || selectedTransaction.etat}
              />
            </Descriptions.Item>

            <Descriptions.Item label={t('Login')} style={{ color: 'var(--primary-color3)' }}>
              <span style={{ color: 'var(--primary-color3)' }}>
                {selectedTransaction.login}
              </span>
            </Descriptions.Item>
          </Descriptions>
        )}

      </Modal>
    </div>
  );
};

export default BalanceTransactions;